import {
  getLocalizations as fetchLocalizations,
  getConfig as fetchConfig,
  getCountries as fetchCountries,
  getAirports as fetchAirports,
  getCarriers as fetchCarriers,
  getFqtv as fetchFqtv,
  getLanguages as fetchLanguages,
} from '../../utils/api/axios';
import _ from 'lodash';
import {
  AirportResource,
  CarrierResource,
  ConfigResource,
  CountryResource,
  FqtvResource,
  LanguageResource,
  LocalizationResource,
  ResourceKeyName,
} from './ResourceInterfaces';
import { store } from '../../app/store';
import {
  resetAllResource,
  resetResourceKey,
  ResourceState,
  setResourceKey,
} from '../../slicers/resource';
import {
  clearAppResourceData,
  setCustomerCode,
  setExpireTime,
  setLangCode,
} from '../../slicers/app';
import { resetCookie } from '../../slicers/cookie';

export default class ResourceService {
  private static instance: ResourceService;
  localizations: LocalizationResource = {} as LocalizationResource;
  config: ConfigResource = {} as ConfigResource;
  airports: AirportResource[] = [];
  countries: CountryResource[] = [];
  languages: LanguageResource[] = [];
  fqtv: FqtvResource[] = [];
  carriers: CarrierResource[] = [];

  /**
   * Check if resource exist in Redux store. If not, fetch the data and dispatch to redux.
   *
   * @param name Resource name
   * @param fetchAPI API function to fetch the resource
   */
  private static async getResourceAsync(
    name: keyof ResourceService,
    fetchAPI: (langCode?: string) => Promise<any>,
    langCode: string
  ) {
    if (!this.instance) {
      this.instance = new ResourceService();
    }
    if (_.isEmpty(this.instance[name])) {
      const typedName = name.toString() as keyof ResourceState;
      // Check if redux store has data, otherwise fetch.
      const storedResource = store.getState().resource[typedName];
      if (_.isObjectLike(storedResource) && !_.isEmpty(storedResource)) {
        try {
          this.instance[name] = storedResource as any;
        } catch (e: any) {
          // Bad data, refetch
          store.dispatch(resetResourceKey(typedName));
          const data = await fetchAPI(langCode);
          this.instance[name] = data;
          store.dispatch(setResourceKey({ key: typedName, value: data }));
        }
      } else {
        const data = await fetchAPI(langCode);
        this.instance[name] = data;
        store.dispatch(setResourceKey({ key: typedName, value: data }));
      }
    }
  }

  /**
   * Load the resource data from Local Storage.
   *
   * @param name Resource name
   * @returns
   */
  private static getResource(name: keyof ResourceService, langCode?: string) {
    if (!this.instance) {
      this.instance = new ResourceService();
    }
    if (_.isEmpty(this.instance[name])) {
      let typedName = name.toString() as keyof ResourceState;
      // Check if local redux store has data
      const storedResource = store.getState().resource[typedName];
      if (!_.isEmpty(storedResource)) {
        // If bad data, remove it so we can load it again next time
        try {
          this.instance[name] = storedResource as any;
        } catch (e: any) {
          store.dispatch(resetResourceKey(typedName));
        }
      }
    }
    return ResourceService.instance[name];
  }

  /**
   * Check if resources is cached (in redux store, which then saved in local storage
   * by redux-persist), if not fetch them.
   *
   * Note: If you see React fetch these data twice, it's because React v18 Strict Mode
   * now will make useEffect run twice. In prod this won't be an issue.
   * @param langCode
   */
  public static async checkResourceInLocalStorage(langCode?: string) {
    let currentCustomer = import.meta.env.VITE_APP_CUSTOMER || 'BWA';
    if (_.isUndefined(langCode)) {
      langCode = 'en';
    }
    this.checkIfResourceExpired(currentCustomer, langCode);

    await this.getResourceAsync(ResourceKeyName.LOCALIZATIONS, fetchLocalizations, langCode);
    await this.getResourceAsync(ResourceKeyName.CONFIG, fetchConfig, langCode);
    await this.getResourceAsync(ResourceKeyName.AIRPORTS, fetchAirports, langCode);
    await this.getResourceAsync(ResourceKeyName.COUNTRIES, fetchCountries, langCode);
    await this.getResourceAsync(ResourceKeyName.LANGUAGES, fetchLanguages, langCode);
    const config = this.getConfig();
    if (config.pages.generic.FQTV_SUPPORTED) {
      await this.getResourceAsync(ResourceKeyName.FQTV, fetchFqtv, langCode);
    }
    await this.getResourceAsync(ResourceKeyName.CARRIERS, fetchCarriers, langCode);

    const DEFAULT_TIME_BEFORE_EXPIRE = 3600; // 60 * 60 or 1 hour
    let timeBeforeExpired = Number(import.meta.env.VITE_APP_LS_EXPIRY_TIME);
    if (!_.isFinite(timeBeforeExpired)) {
      timeBeforeExpired = DEFAULT_TIME_BEFORE_EXPIRE;
    }
    // multiply with 1000 because it's in ms (1000ms = 1s)
    let expireTime = new Date().getTime() + timeBeforeExpired * 1000;

    store.dispatch(setExpireTime(expireTime));
    store.dispatch(setCustomerCode(currentCustomer));
  }

  /**
   * Check if resources is too old (by default 1 hour), belong to wrong customer or
   * in wrong language.
   *
   * @param currentCustomer
   * @param langCode
   */
  private static checkIfResourceExpired(currentCustomer: string, langCode: string) {
    let storedCustomerCode = store.getState().app.customerCode;
    let currentLangCode = store.getState().app.langCode;
    let expire = store.getState().app.expiryTime || 3600; // Time in seconds
    if (
      expire < new Date().getTime() ||
      currentCustomer.toUpperCase() !== storedCustomerCode.toUpperCase() ||
      langCode !== currentLangCode
    ) {
      store.dispatch(resetAllResource());
      store.dispatch(clearAppResourceData());
    }
  }

  public static changeLanguage(newLangCode: string) {
    store.dispatch(resetAllResource());
    store.dispatch(setLangCode(newLangCode));
    const url = new URL(window.location.href);
    url.searchParams.set('langCode', newLangCode);
    window.history.replaceState({}, '', url.toString());
    window.location.reload();
  }

  public static async changeLanguageWithoutReload(newLangCode: string) {
    this.instance[ResourceKeyName.LANGUAGES] = [];
    this.instance[ResourceKeyName.LOCALIZATIONS] = {} as LocalizationResource;
    this.instance[ResourceKeyName.COUNTRIES] = [];
    this.instance[ResourceKeyName.AIRPORTS] = [];
    store.dispatch(setLangCode(newLangCode));
    store.dispatch(resetResourceKey(ResourceKeyName.LANGUAGES));
    store.dispatch(resetResourceKey(ResourceKeyName.LOCALIZATIONS));
    store.dispatch(resetResourceKey(ResourceKeyName.COUNTRIES));
    store.dispatch(resetResourceKey(ResourceKeyName.AIRPORTS));
    await this.getResourceAsync(ResourceKeyName.LANGUAGES, fetchLanguages, newLangCode);
    await this.getResourceAsync(ResourceKeyName.COUNTRIES, fetchCountries, newLangCode);
    await this.getResourceAsync(ResourceKeyName.AIRPORTS, fetchAirports, newLangCode);
    await this.getResourceAsync(ResourceKeyName.CARRIERS, fetchCarriers, newLangCode);
    await this.getResourceAsync(ResourceKeyName.LOCALIZATIONS, fetchLocalizations, newLangCode);
  }

  public static getLocalizations() {
    const data = this.getResource('localizations');
    return data as LocalizationResource;
  }

  public static getConfig() {
    const data = this.getResource('config');
    return data as ConfigResource;
  }

  public static getAirports() {
    const data = this.getResource('airports');
    return data as AirportResource[];
  }

  public static getCarriers() {
    const data = this.getResource('carriers');
    return data as CarrierResource[];
  }

  public static getCountries() {
    const data = this.getResource('countries');
    return data as CountryResource[];
  }

  public static getLanguages() {
    const data = this.getResource('languages');
    return data as LanguageResource[];
  }

  public static getFqtv() {
    const data = this.getResource('fqtv');
    return data as FqtvResource[];
  }
}
